import { DEFAULT_CONFIG, type MatchConfig } from '@nt2/engine';

/**
 * The rules a host last chose, as remembered on the device.
 *
 * Only what differs from the standard game is stored. The whole rulebook used
 * to be saved the first time anybody opened "Change the rules", which froze
 * that day's defaults into the device as though they had been chosen — so
 * when trading letters in was switched off by default, every host who had
 * ever looked at the options would have kept it on without knowing.
 */
export function rulesToRemember(config: MatchConfig): Partial<MatchConfig> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(DEFAULT_CONFIG) as Array<keyof MatchConfig>) {
    if (k === 'seed') continue;              // a reused seed deals the same bag
    if (JSON.stringify(config[k]) !== JSON.stringify(DEFAULT_CONFIG[k])) out[k] = config[k];
  }
  return out as Partial<MatchConfig>;
}

/** The remembered changes laid over today's standard game. */
export function rememberedRules(saved: Partial<MatchConfig>): MatchConfig {
  return {
    ...DEFAULT_CONFIG,
    ...saved,
    peek: { ...DEFAULT_CONFIG.peek, ...(saved.peek ?? {}) },
    visibility: { ...DEFAULT_CONFIG.visibility, ...(saved.visibility ?? {}) },
    seed: DEFAULT_CONFIG.seed,
  };
}

/**
 * Convert rules saved by builds that kept the whole rulebook (run once).
 *
 * Those copies carry the old defaults of the two rules whose defaults have
 * since changed on purpose: trading letters in (was on) and peeking at other
 * grids mid-match (was allowed). Neither could have been a choice — they were
 * simply what was there — so they are let go, and the rest is trimmed to the
 * changes from the standard game.
 */
export function convertOldRules(saved: Partial<MatchConfig>): Partial<MatchConfig> {
  const patch: Partial<MatchConfig> = { ...saved };
  if (patch.dumpEnabled === true) delete patch.dumpEnabled;
  if (patch.visibility?.grids === 'onRequest') {
    patch.visibility = { ...patch.visibility, grids: DEFAULT_CONFIG.visibility.grids };
  }
  return rulesToRemember(rememberedRules(patch));
}
